import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useJ502, ItemState } from './hooks/useJ502';
import { j502Api } from '../../services/j502Api';
import { BatchAcceptanceBridge, pwj502f1AcceptanceManifest } from '../../features/acceptance';
import './J502Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const J502Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  } = useJ502();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆樣品繳庫單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const allSelected =
    items.length > 0 && items.every((s) => s.selected || !s.item.can_approve);

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!record.item.can_approve}
          onChange={(e) =>
            handleToggleItem(record.item.fj120_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '繳庫單號',
      dataIndex: ['item', 'fj120_01'],
      key: 'fj120_01',
      width: 120,
    },
    {
      title: '繳庫日期',
      dataIndex: ['item', 'fj120_11'],
      key: 'fj120_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '庫位',
      key: 'warehouse',
      width: 160,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fj120_12;
        const name = record.item.warehouse_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '部門',
      key: 'dept',
      width: 160,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fj120_14;
        const name = record.item.dept_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '來源申請單',
      dataIndex: ['item', 'fj120_10'],
      key: 'fj120_10',
      width: 120,
    },
    {
      title: '明細筆數',
      dataIndex: ['item', 'fj120_15'],
      key: 'fj120_15',
      width: 90,
      align: 'right',
    },
  ];

  return (
    <div className="j502-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                研發管理
              </a>
              <span>/</span>
              <span className="current">樣品繳庫單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>樣品繳庫單核准</h1>
            <span className="badge">J502</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fj120_01">繳庫單 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fj120_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入繳庫單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            核准 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwj502f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((row) => row.selected).map((row) => row.item.fj120_01)}
          keyOf={(row) => row.item.fj120_01}
          queryExact={(key) => j502Api.query(key, key)}
          lastApiResponse={items}
          loading={loading || executeLoading}
          onAutoFill={() => items[0] && handleToggleItem(items[0].item.fj120_01, true)}
          autoFillDisabled={items.length === 0}
          screenDraft={{ startNo, endNo }}
        />
      </div>
    </div>
  );
};

export default J502Form;
